import { invitePlatformAdmin } from '@jq4race/api-client';
import { useTranslation } from '@jq4race/i18n';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
} from '@jq4race/ui';
import { useEffect, useState } from 'react';

import { translateApiError } from '../../../../shared/errors/translateApiError';

interface InvitePlatformAdminDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onInvited: (email: string) => void;
}

export function InvitePlatformAdminDialog({
  open,
  onOpenChange,
  onInvited,
}: InvitePlatformAdminDialogProps) {
  const { t } = useTranslation('organizations');
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setEmail('');
      setSubmitting(false);
      setError(null);
    }
  }, [open]);

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await invitePlatformAdmin({ email });
      onInvited(email);
      onOpenChange(false);
    } catch (err) {
      setError(
        err instanceof Error
          ? translateApiError(err, t, { context: 'platform' })
          : t('invitePlatformAdminDialog.error'),
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('invitePlatformAdminDialog.title')}</DialogTitle>
          <DialogDescription>{t('invitePlatformAdminDialog.description')}</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="mt-6 space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="invite-platform-admin-email">
              {t('invitePlatformAdminDialog.emailLabel')}
            </Label>
            <Input
              id="invite-platform-admin-email"
              type="email"
              required
              autoFocus
              placeholder={t('invitePlatformAdminDialog.emailPlaceholder')}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={submitting}
            />
          </div>

          {error && (
            <p className="rounded-sm border border-jq-danger/40 bg-jq-danger/10 px-3 py-2 text-[13px] text-jq-danger">
              {error}
            </p>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => onOpenChange(false)}
              disabled={submitting}
            >
              {t('invitePlatformAdminDialog.cancel')}
            </Button>
            <Button type="submit" size="sm" disabled={submitting}>
              {submitting
                ? t('invitePlatformAdminDialog.submitting')
                : t('invitePlatformAdminDialog.submit')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
